import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Snowflake, Megaphone } from "@phosphor-icons/react";

const REASONS = ["Weather", "Snow day", "Ice storm", "Power outage", "Illness", "Staff shortage",
                 "Emergency", "Holiday"];

const DEFAULT_MSG = "Teach Play to Learn will be closed today. Please keep your child home — "
  + "we will reopen at our normal 6:00 AM opening tomorrow unless we let you know otherwise. "
  + "Stay safe!";

export const SnowDayButton = ({ onDone, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    reason: "Snow day",
    message: DEFAULT_MSG,
    mark_absent: true,
  });

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/closures", f);
      toast.success(`Closure sent to ${data.families_notified} families · `
        + `${data.students_marked_absent} students marked absent`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}
              className={`rounded-full h-12 px-7 bg-[#EF4444] hover:bg-red-600 font-bold active:scale-95 ${className}`}
              style={{ transition: "transform .2s ease, background-color .2s ease" }}
              data-testid="snow-day-btn">
        <Snowflake size={18} className="mr-2" /> Close the center
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl" data-testid="closure-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Close the center for a day</DialogTitle>
            <DialogDescription>
              This adds a closure to the calendar, posts an announcement, marks every active student
              absent, and immediately notifies every family by email and text.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold">Date</Label>
                <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="closure-date" />
              </div>
              <div>
                <Label className="text-sm font-bold">Reason</Label>
                <Select value={f.reason} onValueChange={(v) => setF({ ...f, reason: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="closure-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold">Message to families</Label>
              <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
                        className="mt-2 rounded-xl min-h-32" data-testid="closure-message" />
            </div>
            <label className="flex items-center gap-3 text-sm font-semibold">
              <input type="checkbox" checked={f.mark_absent}
                     onChange={(e) => setF({ ...f, mark_absent: e.target.checked })}
                     data-testid="closure-mark-absent" />
              Mark every active student absent for that day
            </label>
            <Button onClick={send} disabled={busy || !f.message.trim()}
                    className="w-full h-12 rounded-full bg-[#EF4444] hover:bg-red-600 font-bold"
                    data-testid="closure-send-btn">
              <Megaphone size={18} className="mr-2" />
              {busy ? "Notifying families…" : "Close & notify every family"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
